import { HStack, Text } from '@chakra-ui/react';
import { useLazyRetrieveCoordinatesAltitudeQuery } from '@services/c2/c2.api';
import { convertDistanceUnitFromMeter, HeightUnits, unitLabel } from '@utils/unit.utils';
import DmsCoordinates from 'dms-conversion';
import { debounce } from 'lodash';
import { useEffect, useMemo } from 'react';

import { Coordinates } from '@/types/commons/commons.types';

type CoordinatesBarProps = {
  coordinatesDMS: DmsCoordinates;
};

function AltitudeBar({ coordinatesDMS }: Readonly<CoordinatesBarProps>) {
  const [retrieve, { data: altitudes = [], reset }] = useLazyRetrieveCoordinatesAltitudeQuery();
  const altitude: number | undefined = altitudes.length >= 1 ? altitudes[0] : undefined;

  const debouncedTrigger = useMemo(
    () =>
      debounce((coords: Coordinates) => {
        retrieve({ coordinates: [coords] });
      }, 600),
    [retrieve],
  );

  useEffect(() => {
    reset();
    debouncedTrigger({ latitude: coordinatesDMS.latitude.dd, longitude: coordinatesDMS.longitude.dd, type: '2D' });
  }, [coordinatesDMS, debouncedTrigger, reset]);

  useEffect(() => {
    return () => {
      debouncedTrigger.cancel();
    };
  }, [debouncedTrigger]);

  return (
    <HStack
      gap={2}
      width={{ base: '165px', '4k': '180px' }}
      flexShrink={0}
      height={{ base: '32px', '4k': '40px' }}
      backgroundColor="neutral.800-op70"
      paddingX={2}
      paddingY={0.25}
      justifyContent="center"
    >
      <HStack gap={1}>
        <Text width="100%">{altitude ?? '-'}</Text>
        <Text color="neutral.300">{unitLabel[HeightUnits.METERS]}</Text>
      </HStack>
      <HStack gap={1}>
        <Text width="100%">{convertDistanceUnitFromMeter(HeightUnits.FEET, altitude, 0) ?? '-'}</Text>
        <Text color="neutral.300">{unitLabel[HeightUnits.FEET]}</Text>
      </HStack>
    </HStack>
  );
}

export default AltitudeBar;
